import { AxiosRequestConfig } from 'axios';
import { Map } from 'immutable';
import { ReturnOnly } from './ReturnOnly';

/**
 * Clean data response
 * @example
 * ```js
 * const response = ( data: any ) => ( {
 *     config:  {},
 *     request: {},
 *     status:  200,
 *     data
 * } );
 *
 * cleanDataResponse(response({ data: { message: 'test' } })) // { message: 'test' }
 * cleanDataResponse(response({ data: { data: { message: 'test' } } })) // { message: 'test' }
 * ```
 * @param response
 */
export const cleanDataResponse = (response: any): any => {
    if (response) {
        const keys = Object.keys(response);

        if (
            response.data &&
            (keys.length === 1 ||
                (keys.includes('config') && keys.includes('status') && keys.includes('request')) ||
                (keys.length === 3 && keys.includes('code') && keys.includes('data') && keys.includes('status')))
        ) {
            return cleanDataResponse(response.data);
        }
    }

    return response;
};

/**
 * Get header from an IncomingMessage instance or other format.
 * @param data
 * @param name
 */
export const header = (data: any, name: string): string | null =>
    (data?.req?.headers ?? {})[name] || (data?.headers ?? {})[name] || (data ?? {})[name] || null;

/**
 * Get header from an Object.
 * @param data
 * @param name
 */
export const param = (data: any, name: string): string | null =>
    (data?.query ?? {})[name] || (data?.params ?? {})[name] || (data ?? {})[name] || null;

/**
 * Merge axios configs
 * @param target
 * @param source
 */
export const mergeConfig = (
    target?: AxiosRequestConfig,
    ...source: (AxiosRequestConfig | undefined)[]
): AxiosRequestConfig => {
    return source.reduce((_target, source) => _target.mergeDeep(Map(source as any)), Map((target ?? {}) as any)).toJS();
};

/**
 * Add fields to an axios config
 * @param fields
 * @param config
 */
export const fields = (fields: string | (string | any)[], config?: AxiosRequestConfig) =>
    mergeConfig(
        {
            params: {
                fields,
            },
        },
        config,
    );

/**
 * A curry function used to merge multiple partial fields.
 * For example fields()('id,name,image', 'image{id, file_path}'),
 * should return a new fields like 'id, name, image{id, file_path}'
 * @param config
 */
export const fieldsMerge =
    (config?: AxiosRequestConfig) =>
    (...fields: any[]) =>
        mergeConfig(
            {
                params: {
                    fields: ReturnOnly.merge({}, ...fields),
                },
            },
            config,
        );
